import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import VideoPlayer from '../components/VideoPlayer';
import './CourseDetail.css';

const CourseDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [enrolled, setEnrolled] = useState(false);
  const [courseCompleted, setCourseCompleted] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [generatingCertificate, setGeneratingCertificate] = useState(false);
  const [quizzes, setQuizzes] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [courseStudents, setCourseStudents] = useState([]);

  // Check if user is admin or instructor (they don't enroll, they manage)
  const isAdminOrInstructor = user?.role === 'admin' || user?.role === 'sub-admin' || user?.role === 'instructor';
  const canManageCourse = isAdminOrInstructor && (user?.role === 'admin' || user?.role === 'sub-admin' || course?.instructor_id === user?.id);

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        const courseRes = await api.get(`/courses/${id}`);
        setCourse(courseRes.data);

        if (user) {
          // Check if enrolled
          const enrollmentRes = await api.get('/enrollments/my-courses');
          const enrollment = enrollmentRes.data.find(
            (e) => e.course_id === parseInt(id)
          );
          const isEnrolled = !!enrollment;
          setEnrolled(isEnrolled);

          // Check if course is completed
          if (enrollment && enrollment.completed_at) {
            setCourseCompleted(true);
            // Fetch certificate if exists
            try {
              const certRes = await api.get('/certificates/my-certificates');
              const courseCert = certRes.data.find(c => c.course_id === parseInt(id));
              if (courseCert) {
                setCertificate(courseCert);
              }
            } catch (certError) {
              console.error('Error fetching certificate:', certError);
            }
          }

          // Get progress if enrolled
          if (isEnrolled) {
            const progressRes = await api.get(`/progress/course/${id}`);
            setProgress(progressRes.data);
          }

          // Fetch quizzes and assignments for enrolled users or instructors
          if (isEnrolled || user.role === 'instructor' || user.role === 'admin' || user.role === 'sub-admin') {
            try {
              const quizzesRes = await api.get(`/quizzes/course/${id}`);
              setQuizzes(quizzesRes.data || []);
            } catch (quizError) {
              console.error('Error fetching quizzes:', quizError);
            }

            try {
              const assignmentsRes = await api.get(`/assignments/course/${id}`);
              setAssignments(assignmentsRes.data || []);
            } catch (assignmentError) {
              console.error('Error fetching assignments:', assignmentError);
            }
          }

          // Fetch enrolled students if admin or instructor
          if (canManageCourse) {
            try {
              const studentsRes = await api.get(`/courses/${id}/students`);
              setCourseStudents(studentsRes.data || []);
            } catch (studentsError) {
              console.error('Error fetching course students:', studentsError);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching course:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [id, user]);

  const handleEnroll = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    const price = course?.sale_price || course?.price;
    if (price && parseFloat(price) > 0 && !course?.is_free) {
      navigate(`/checkout/${id}`);
      return;
    }

    try {
      await api.post('/enrollments', { course_id: id });
      setEnrolled(true);
      window.location.reload();
    } catch (error) {
      console.error('Error enrolling:', error);
      alert('Failed to enroll in course');
    }
  };

  const handleVideoComplete = async (videoId) => {
    try {
      await api.post(`/progress/video/${videoId}`, {
        completed: true,
        watch_time: 0,
      });

      // Update progress locally
      const updatedProgress = progress.map((p) =>
        p.video_id === videoId ? { ...p, completed: true } : p
      );
      setProgress(updatedProgress);

      // Check if all videos are now completed
      const allCompleted = updatedProgress.every((p) => p.completed);
      if (allCompleted && course?.videos?.length > 0) {
        // Auto-complete the course
        try {
          await api.post(`/enrollments/${id}/complete`);
          setCourseCompleted(true);
        } catch (completeError) {
          console.error('Error completing course:', completeError);
        }
      }
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const handleGenerateCertificate = async () => {
    setGeneratingCertificate(true);
    try {
      const res = await api.post(`/certificates/generate/${id}`);
      setCertificate(res.data);
    } catch (error) {
      console.error('Error generating certificate:', error);
      alert('Failed to generate certificate');
    } finally {
      setGeneratingCertificate(false);
    }
  };

  const handleDownloadCertificate = async () => {
    try {
      const response = await api.get(`/certificates/download/${certificate.id}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate_${certificate.certificate_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('Failed to download certificate');
    }
  };

  // Check if a video is unlocked (sequential unlocking)
  const isVideoUnlocked = (video, index) => {
    if (!enrolled) return false;
    if (index === 0) return true; // First video is always unlocked

    // Check if all previous videos are completed
    const sortedVideos = [...(course?.videos || [])].sort(
      (a, b) => a.order_index - b.order_index
    );

    for (let i = 0; i < index; i++) {
      const prevVideo = sortedVideos[i];
      const prevProgress = progress.find((p) => p.video_id === prevVideo.id);
      if (!prevProgress?.completed) {
        return false;
      }
    }
    return true;
  };

  // Calculate progress percentage
  const completedVideos = progress.filter((p) => p.completed).length;
  const totalVideos = course?.videos?.length || 0;
  const progressPercentage = totalVideos > 0 ? Math.round((completedVideos / totalVideos) * 100) : 0;

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!course) {
    return <div className="page">Course not found</div>;
  }

  const videoProgress = progress.find((p) => p.video_id === selectedVideo?.id);

  return (
    <div className="page">
      <div className="course-detail-container">
        <div className="course-header">
          <h1>{course.title}</h1>
          <p className="course-category">{course.category_name}</p>
          <p className="course-instructor">
            Instructor: {course.instructor_name}
          </p>
          <p className="course-description">{course.description}</p>

          {/* Admin/Instructor view - Edit button instead of enroll */}
          {isAdminOrInstructor ? (
            <div className="admin-actions">
              <div className="admin-badge">
                {user?.role === 'admin' || user?.role === 'sub-admin' ? 'Admin View' : 'Instructor View'}
              </div>
              <button
                onClick={() => navigate(`/edit-course/${id}`)}
                className="btn-primary edit-course-btn"
              >
                Edit Course
              </button>
            </div>
          ) : (
            <>
              {!enrolled ? (
                <button onClick={handleEnroll} className="btn-primary enroll-btn">
                  Enroll in Course
                </button>
              ) : (
                <div className="enrollment-status">
                  <div className="enrolled-badge">Enrolled</div>
                  {totalVideos > 0 && (
                    <div className="progress-container">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${progressPercentage}%` }}
                        ></div>
                      </div>
                      <span className="progress-text">
                        {completedVideos} / {totalVideos} lessons completed ({progressPercentage}%)
                      </span>
                    </div>
                  )}
                </div>
              )}

              {courseCompleted && (
                <div className="certificate-section">
                  <div className="course-completed-badge">Course Completed!</div>
                  {certificate ? (
                    <div className="certificate-info">
                      <p>Certificate Number: {certificate.certificate_number}</p>
                      <button
                        onClick={handleDownloadCertificate}
                        className="btn-primary download-cert-btn"
                      >
                        Download Certificate
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleGenerateCertificate}
                      className="btn-primary generate-cert-btn"
                      disabled={generatingCertificate}
                    >
                      {generatingCertificate ? 'Generating...' : 'Generate Certificate'}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {selectedVideo && (
          <div className="video-player-section">
            <VideoPlayer
              video={selectedVideo}
              onComplete={() => handleVideoComplete(selectedVideo.id)}
              isCompleted={videoProgress?.completed}
            />
          </div>
        )}

        <div className="videos-section">
          <h2>Course Content</h2>
          {course.videos && course.videos.length > 0 ? (
            <div className="video-list">
              {[...course.videos]
                .sort((a, b) => a.order_index - b.order_index)
                .map((video, index) => {
                  const vidProgress = progress.find((p) => p.video_id === video.id);
                  const unlocked = isVideoUnlocked(video, index);
                  // Admin/Instructor can always click, students need to be enrolled and have video unlocked
                  const canClick = isAdminOrInstructor || (enrolled && unlocked);

                  return (
                    <div
                      key={video.id}
                      className={`video-item ${selectedVideo?.id === video.id ? 'active' : ''
                        } ${!unlocked && enrolled && !isAdminOrInstructor ? 'locked' : ''}`}
                      onClick={() => canClick && setSelectedVideo(video)}
                      style={{ cursor: canClick ? 'pointer' : 'not-allowed' }}
                    >
                      <div className={`video-number ${vidProgress?.completed ? 'completed' : ''}`}>
                        {vidProgress?.completed ? '✓' : index + 1}
                      </div>
                      <div className="video-info">
                        <h4>{video.title}</h4>
                        <p>{video.description}</p>
                        {video.duration && (
                          <span className="video-duration">
                            {Math.floor(video.duration / 60)} min
                          </span>
                        )}
                        {!unlocked && enrolled && !isAdminOrInstructor && (
                          <span className="unlock-hint">Complete previous lessons to unlock</span>
                        )}
                      </div>
                      {!enrolled && !isAdminOrInstructor && <div className="locked-icon">🔒</div>}
                      {enrolled && !unlocked && !isAdminOrInstructor && <div className="locked-icon">🔒</div>}
                    </div>
                  );
                })}
            </div>
          ) : (
            <p>No videos available yet.</p>
          )}
        </div>

        {/* Quizzes Section - for enrolled students and instructors */}
        {(enrolled || isAdminOrInstructor) && (
          <div className="quizzes-section">
            <div className="section-header">
              <h2>Quizzes</h2>
              {canManageCourse && (
                <button
                  className="btn-primary add-btn"
                  onClick={() => navigate(`/courses/${id}/quiz/new`)}
                >
                  + Add Quiz
                </button>
              )}
            </div>
            {quizzes.length > 0 ? (
              <div className="assessment-grid">
                {quizzes.map((quiz) => (
                  <div key={quiz.id} className="assessment-card quiz-card">
                    <div className="assessment-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    </div>
                    <div className="assessment-info">
                      <h4>{quiz.title}</h4>
                      {quiz.description && <p className="assessment-desc">{quiz.description}</p>}
                      <div className="assessment-meta">
                        <span className="meta-item">
                          {quiz.question_count || 0} questions
                        </span>
                        <span className="meta-item">
                          Pass: {quiz.passing_score || 70}%
                        </span>
                        {quiz.time_limit > 0 && (
                          <span className="meta-item">
                            {Math.floor(quiz.time_limit / 60)} min
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="assessment-actions">
                      <button
                        className="btn-primary take-btn"
                        onClick={() => navigate(`/quiz/${quiz.id}`)}
                      >
                        {canManageCourse ? 'Preview' : 'Take Quiz'}
                      </button>
                      {canManageCourse && (
                        <button
                          className="btn-secondary edit-btn"
                          onClick={() => navigate(`/quiz/${quiz.id}/manage`)}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-assessment">
                <p>No quizzes available yet.</p>
              </div>
            )}
          </div>
        )}

        {/* Assignments Section - for enrolled students and instructors */}
        {(enrolled || isAdminOrInstructor) && (
          <div className="assignments-section">
            <div className="section-header">
              <h2>Assignments</h2>
              {canManageCourse && (
                <button
                  className="btn-primary add-btn"
                  onClick={() => navigate(`/courses/${id}/assignments`)}
                >
                  Manage Assignments
                </button>
              )}
            </div>
            {assignments.length > 0 ? (
              <div className="assessment-grid">
                {assignments.map((assignment) => (
                  <div key={assignment.id} className="assessment-card assignment-card">
                    <div className="assessment-icon assignment-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="assessment-info">
                      <h4>{assignment.title}</h4>
                      {assignment.description && <p className="assessment-desc">{assignment.description}</p>}
                      <div className="assessment-meta">
                        <span className="meta-item">
                          Max Score: {assignment.max_score || 100}
                        </span>
                        {assignment.due_date && (
                          <span className="meta-item due-date">
                            Due: {new Date(assignment.due_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="assessment-actions">
                      {!canManageCourse ? (
                        <button
                          className="btn-primary submit-btn"
                          onClick={() => navigate(`/assignment/${assignment.id}/submit`)}
                        >
                          Submit
                        </button>
                      ) : (
                        <button
                          className="btn-primary view-btn"
                          onClick={() => navigate(`/assignment/${assignment.id}/submissions`)}
                        >
                          View Submissions
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-assessment">
                <p>No assignments available yet.</p>
              </div>
            )}
          </div>
        )}

        {/* Community Section - for enrolled students and instructors */}
        {(enrolled || isAdminOrInstructor) && (
          <div className="community-section">
            <div className="section-header">
              <h2>Community & Communication</h2>
            </div>
            <div className="community-grid">
              <Link to={`/courses/${id}/announcements`} className="community-card announcements-card">
                <div className="community-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                </div>
                <div className="community-info">
                  <h4>Announcements</h4>
                  <p>Stay updated with course news and important updates from your instructor.</p>
                </div>
                <div className="community-arrow">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </Link>

              <Link to={`/courses/${id}/questions`} className="community-card questions-card">
                <div className="community-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    <path d="M8 9h8M8 13h6" />
                  </svg>
                </div>
                <div className="community-info">
                  <h4>Q&A Forum</h4>
                  <p>Ask questions, get answers from instructors and fellow students.</p>
                </div>
                <div className="community-arrow">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </Link>

              {canManageCourse && (
                <Link to={`/courses/${id}/manage-announcements`} className="community-card manage-card">
                  <div className="community-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                  </div>
                  <div className="community-info">
                    <h4>Manage Announcements</h4>
                    <p>Create and manage announcements for your students.</p>
                  </div>
                  <div className="community-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </div>
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Enrolled Students Section - Admin/Instructor Only */}
        {canManageCourse && (
          <div className="enrolled-students-section">
            <div className="section-header">
              <h2>Enrolled Students</h2>
              <span className="student-count-badge">
                {courseStudents.length} {courseStudents.length === 1 ? 'Student' : 'Students'}
              </span>
            </div>

            {courseStudents.length > 0 ? (
              <div className="students-table-container">
                <table className="students-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Enrolled Date</th>
                      <th>Progress</th>
                      <th>Quiz Avg</th>
                      <th>Lessons</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courseStudents.map((student) => (
                      <tr key={student.user_id}>
                        <td>
                          <div className="student-info-cell">
                            <div className="student-avatar">
                              {student.avatar_url ? (
                                <img src={student.avatar_url} alt={student.first_name} />
                              ) : (
                                <div className="avatar-placeholder">
                                  {student.first_name?.[0] || student.email[0].toUpperCase()}
                                </div>
                              )}
                            </div>
                            <div className="student-details">
                              <strong>{student.first_name} {student.last_name}</strong>
                              <span className="student-email">{student.email}</span>
                            </div>
                          </div>
                        </td>
                        <td>{new Date(student.enrolled_at).toLocaleDateString()}</td>
                        <td>
                          <div className="progress-cell">
                            <div className="progress-bar small">
                              <div
                                className="progress-fill"
                                style={{ width: `${student.progress_percentage || 0}%` }}
                              />
                            </div>
                            <span className="progress-text-small">{Math.round(student.progress_percentage || 0)}%</span>
                          </div>
                        </td>
                        <td>{Math.round(student.avg_quiz_score || 0)}%</td>
                        <td>{student.lessons_completed}/{student.total_lessons}</td>
                        <td>
                          <span className={`status-badge ${student.completed_at ? 'completed' : 'in-progress'}`}>
                            {student.completed_at ? 'Completed' : 'Active'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state large">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <h3>No Students Yet</h3>
                <p>There are currently no students enrolled in this course.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseDetail;
